import { HELP_EMERGENCY_LOCALE, isSupportedLocale } from '@erp/shared';

export interface HelpMetadata {
  readonly id: string;
  readonly locale: string;
  readonly route: string;
  readonly contentVersion: number;
  readonly translatedFromVersion: number;
  readonly customerSafe: boolean;
  readonly permissions: readonly string[];
  readonly title: string;
  readonly description: string;
  readonly keywords: readonly string[];
  readonly related: readonly string[];
}

export interface HelpArticle {
  readonly metadata: HelpMetadata;
  readonly body: string;
  readonly category: string;
  readonly resolvedLocale: string;
  readonly usedFallback: boolean;
}

export interface HelpTranslationStatus {
  readonly sourceCount: number;
  readonly localeCount: number;
  readonly currentCount: number;
  readonly fallbackCount: number;
  readonly staleCount: number;
}

type MarkdownLoader = () => Promise<string>;

// `lib` is six levels below the repository root; keep the explicit locale set
// so retired help trees cannot leak back into the active bundle.
const markdownFiles = import.meta.glob<string>(
  '../../../../../../docs/help/{de,en,es,fr,tr}/**/*.md',
  {
    query: '?raw',
    import: 'default',
  },
) as Record<string, MarkdownLoader>;

export const DEFAULT_HELP_ARTICLE_ID = 'getting-started.navigation';

function list(value: string | undefined): readonly string[] {
  return value === undefined || value === ''
    ? []
    : value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
}

export function parseHelpArticle(
  source: string,
  resolvedLocale: string,
  usedFallback = false,
  category = 'general',
): HelpArticle {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/u.exec(source);
  if (match === null) throw new Error('HELP_ARTICLE_INVALID');
  const frontmatter = match[1] ?? '';
  const body = match[2] ?? '';
  const values = new Map<string, string>();
  for (const line of frontmatter.split(/\r?\n/u)) {
    const separator = line.indexOf(':');
    if (separator > 0)
      values.set(line.slice(0, separator).trim(), line.slice(separator + 1).trim());
  }
  const required = (key: string): string => {
    const value = values.get(key);
    if (value === undefined) throw new Error(`HELP_ARTICLE_METADATA_${key.toUpperCase()}_MISSING`);
    return value;
  };
  return {
    metadata: {
      id: required('id'),
      locale: required('locale'),
      route: required('route'),
      contentVersion: Number(required('contentVersion')),
      translatedFromVersion: Number(required('translatedFromVersion')),
      customerSafe: required('customerSafe') === 'true',
      permissions: list(values.get('permissions')),
      title: required('title'),
      description: required('description'),
      keywords: list(values.get('keywords')),
      related: list(values.get('related')),
    },
    body: body.trim(),
    category,
    resolvedLocale,
    usedFallback,
  };
}

export function helpLocaleChain(userLocale: string, tenantLocale: string): readonly string[] {
  const exact = userLocale.trim();
  const base = exact.split('-')[0] ?? exact;
  const tenantExact = tenantLocale.trim();
  const tenantBase = tenantExact.split('-')[0] ?? tenantExact;
  return [...new Set([exact, base, tenantExact, tenantBase, HELP_EMERGENCY_LOCALE])].filter(
    (locale) => {
      const candidateBase = locale.split('-')[0];
      return (
        locale.length > 0 &&
        (isSupportedLocale(locale) ||
          (candidateBase !== undefined && isSupportedLocale(candidateBase)))
      );
    },
  );
}

interface HelpLoader {
  readonly category: string;
  readonly loader: MarkdownLoader;
}

function loadersForLocale(locale: string): readonly HelpLoader[] {
  const marker = `/docs/help/${locale}/`;
  return Object.entries(markdownFiles)
    .filter(([path]) => path.includes(marker))
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([path, loader]) => {
      const relativePath = path.split(marker)[1] ?? '';
      const category = relativePath.includes('/')
        ? (relativePath.split('/')[0] ?? 'general')
        : 'general';
      return { category, loader };
    });
}

export async function loadHelpArticles(
  userLocale: string,
  tenantLocale: string,
  allowFallback = true,
): Promise<readonly HelpArticle[]> {
  const chain = allowFallback
    ? helpLocaleChain(userLocale, tenantLocale)
    : [configuredHelpLocale(userLocale)];
  const resolved = new Map<string, HelpArticle>();
  for (const [index, locale] of chain.entries()) {
    const loaded = await Promise.all(
      loadersForLocale(locale).map(async ({ category, loader }) =>
        parseHelpArticle(await loader(), locale, index > 0, category),
      ),
    );
    for (const article of loaded) {
      if (!resolved.has(article.metadata.id)) {
        resolved.set(article.metadata.id, article);
      }
    }
  }
  return [...resolved.values()];
}

export async function loadHelpTranslationStatus(
  articles: readonly HelpArticle[],
): Promise<HelpTranslationStatus> {
  const allArticles = await Promise.all(
    Object.entries(markdownFiles)
      .filter(([path]) => {
        const locale = /\/docs\/help\/([^/]+)\//u.exec(path)?.[1];
        return locale !== undefined && isSupportedLocale(locale);
      })
      .map(async ([path, loader]) => {
        const locale = /\/docs\/help\/([^/]+)\//u.exec(path)?.[1] ?? HELP_EMERGENCY_LOCALE;
        return parseHelpArticle(await loader(), locale);
      }),
  );
  const sourceArticles = allArticles.filter(
    (article) => article.metadata.locale === HELP_EMERGENCY_LOCALE,
  );
  const localeCount = new Set(allArticles.map((article) => article.metadata.locale)).size;
  return {
    sourceCount: sourceArticles.length,
    localeCount,
    currentCount: articles.filter((article) => !article.usedFallback).length,
    fallbackCount: articles.filter((article) => article.usedFallback).length,
    staleCount: allArticles.filter(
      (article) => article.metadata.translatedFromVersion < article.metadata.contentVersion,
    ).length,
  };
}

export function filterHelpArticles(
  articles: readonly HelpArticle[],
  options: {
    readonly customerMode: boolean;
    readonly permissions: readonly string[];
    readonly isSuperAdmin: boolean;
  },
): readonly HelpArticle[] {
  return articles.filter(
    (article) =>
      (!options.customerMode || article.metadata.customerSafe) &&
      (options.customerMode ||
        options.isSuperAdmin ||
        article.metadata.permissions.every(
          (permission) =>
            permission === 'authenticated' || options.permissions.includes(permission),
        )),
  );
}

export function selectHelpArticle(
  articles: readonly HelpArticle[],
  articleId: string | null,
  pathname: string,
): HelpArticle | undefined {
  return (
    articles.find((article) => article.metadata.id === articleId) ??
    [...articles]
      .filter(
        (article) =>
          pathname === article.metadata.route || pathname.startsWith(`${article.metadata.route}/`),
      )
      .sort((left, right) => right.metadata.route.length - left.metadata.route.length)[0] ??
    articles.find((article) => article.metadata.id === DEFAULT_HELP_ARTICLE_ID) ??
    articles[0]
  );
}

export function configuredHelpLocale(locale: string): string {
  const base = locale.split('-')[0];
  return isSupportedLocale(locale)
    ? locale
    : base !== undefined && isSupportedLocale(base)
      ? base
      : HELP_EMERGENCY_LOCALE;
}
